import { apiClient } from "./client";
import type { components } from "../api-schema.generated";
import type { CatalogueAddressBook, CollectionPoint } from "../api-types";
import { deliveryFromWire } from "../catalogue-delivery-wire";
import { deliverySelectionBody } from "../delivery-selection-body";
import type {
  CatalogueItemListOptions,
  ListOptions,
  ListResponse,
} from "../adapter";
import type { PriceLevels } from "../price-precedent-types";
import type { QuoteLine } from "../pricing-quote";
import type {
  OptionArrival,
  SpeedChoice,
  SpeedLadderRung,
} from "../speed-types";
import type {
  ArtworkMode,
  Catalogue,
  CatalogueCostLineage,
  CatalogueRecord,
  CatalogueCostRefreshRun,
  CatalogueDeliverySelection,
  CatalogueField,
  CatalogueItem,
  CatalogueItemPresentationInput,
  CatalogueItemPricePreview,
  CatalogueItemTile,
  CatalogueItemVariantWithCosts,
  CatalogueView,
  CreateCatalogueRequest,
  CustomerCatalogueSettings,
  CustomValueConstraints,
  DeriveCatalogueItemInput,
  DictionaryItem,
  DictionaryItemDetail,
  DictionaryWorkbookScope,
  FieldControl,
  FieldMode,
  FieldNarrowing,
  FulfilmentType,
  Narrowing,
  PriceProvenance,
  QuantityBreak,
  VariantQuantityCost,
} from "../catalogue-types";
import type {
  CataloguePriceOption,
  CataloguePriceOptions,
  PriceOptionChoice,
  PriceOptionDelivery,
  RouteNarrowing,
} from "../supplier-pricing-types";

type ManagementResult = { catalogue: Catalogue; item: CatalogueItem };

type CatalogueSchema = components["schemas"]["CatalogueSchema"];
type CatalogueRecordSchema = components["schemas"]["CatalogueRecordSchema"];
type PaginatedCatalogueRows =
  components["schemas"]["Paginated_CatalogueRowSchema_"];
type CatalogueItemSchema = components["schemas"]["CatalogueItemSchema"];
type CatalogueWithItemsSchema =
  components["schemas"]["CatalogueWithItemsSchema"];
type ResolvedCatalogueItemSchema =
  components["schemas"]["ResolvedCatalogueItemSchema"];
type CustomerCatalogueSettingsResponse =
  components["schemas"]["CustomerCatalogueSettingsResponse"];
export type CatalogueFieldSchema =
  components["schemas"]["CatalogueFieldSchema"];
type CatalogueItemVariantSchema =
  components["schemas"]["DictionaryItemVariantSchema"];
type DictionaryItemSchema = components["schemas"]["DictionaryItemSchema"];
type DictionaryItemDetailSchema =
  components["schemas"]["DictionaryItemDetailSchema"];
type NarrowingSchema = components["schemas"]["Narrowing"];
type RouteNarrowingSchema = components["schemas"]["RouteNarrowing"];
type PriceOptionsSchema = components["schemas"]["PriceOptionsSchema"];
type PriceOptionSchema = components["schemas"]["PriceOptionSchema"];
type OptionArrivalSchema = components["schemas"]["OptionArrivalSchema"];
type SpeedChoiceSchema = components["schemas"]["SpeedChoiceSchema"];
type CatalogueSpeedSchema = components["schemas"]["CatalogueSpeedSchema"];
type PriceOptionDeliverySchema =
  components["schemas"]["PriceOptionDeliverySchema"];
type RouteNarrowingConsequencesResponse =
  components["schemas"]["RouteNarrowingConsequencesResponse"];
type VariantQuantityCostSchema =
  components["schemas"]["VariantQuantityCostSchema"];
type CostRefreshRunStatusSchema =
  components["schemas"]["CostRefreshRunStatusSchema"];
type CostRefreshAcceptedResponse =
  components["schemas"]["CostRefreshAcceptedResponse"];
type CostLineageSchema = components["schemas"]["CostLineageSchema"];
type PricePreviewResponseSchema =
  components["schemas"]["PricePreviewResponseSchema"];
type PriceLevelsWire =
  components["schemas"]["catalogue_module__api__schemas__catalogue__PriceLevelsSchema"];
type CatalogueItemTileSchema = components["schemas"]["CatalogueItemTileSchema"];
type PaginatedCatalogueItemTiles =
  components["schemas"]["Paginated_CatalogueItemTileSchema_"];

export async function listCatalogues(
  orderingCustomerCode?: string,
): Promise<Catalogue[]> {
  const schemas = await apiClient.get<CatalogueSchema[]>(
    orderingCustomerCode === undefined
      ? "catalogue/catalogues"
      : `catalogue/catalogues?${new URLSearchParams({ ordering_customer_code: orderingCustomerCode })}`,
  );
  return schemas.map(catalogueFromSchema);
}

/** Every catalogue, for the Settings pages: the catalogue object's list, read in one page (there are tens). */
export async function listCataloguesForManagement(): Promise<Catalogue[]> {
  const page = await apiClient.get<PaginatedCatalogueRows>(
    "catalogue/manage/catalogues?page_size=200",
  );
  return page.items.map(catalogueFromSchema);
}

/** One catalogue as its record shows it: ownership, what the editor may change, and the commands on it. */
export async function getCatalogueRecord(
  catalogueId: string,
): Promise<CatalogueRecord> {
  const s = await apiClient.get<CatalogueRecordSchema>(
    `catalogue/manage/catalogues/${encodeURIComponent(catalogueId)}`,
  );
  return {
    catalogueId: s.catalogue_id,
    name: s.name,
    ownedBy: s.owned_by,
    ownership: s.ownership,
    editability: {
      canAddItems: s.editability.can_add_items,
      canChangeRouteNarrowing: s.editability.can_change_route_narrowing,
      editableItemFields: s.editability.editable_item_fields,
    },
    commands: s.commands as CatalogueRecord["commands"],
  };
}

/** The dictionary workbook, built for the person's session; `catalogue_id` repeats, in the order the sheets go. */
export async function downloadDictionaryWorkbook(
  scope: DictionaryWorkbookScope,
): Promise<{ blob: Blob; filename: string }> {
  const query = new URLSearchParams([
    ...scope.catalogueIds.map((id): [string, string] => ["catalogue_id", id]),
    ["wording", scope.wording],
  ]);
  const { blob, filename } = await apiClient.getBlob(
    `catalogue/workbook/export?${query}`,
  );
  return { blob, filename };
}

export async function createCatalogue(
  req: CreateCatalogueRequest,
): Promise<Catalogue> {
  const schema = await apiClient.post<CatalogueSchema>("catalogue/catalogues", {
    name: req.name,
    private_to_customer_code: req.privateToCustomerCode ?? null,
  });
  return catalogueFromSchema(schema);
}

export async function getCatalogueWithItems(
  catalogueId: string,
): Promise<CatalogueView | null> {
  try {
    const schema = await apiClient.get<CatalogueWithItemsSchema>(
      `catalogue/catalogues/${catalogueId}`,
    );
    return {
      catalogue: catalogueFromSchema(schema.catalogue),
      items: schema.items.map(itemTileFromSchema),
    };
  } catch (err: unknown) {
    if (err && typeof err === "object" && "status" in err && err.status === 404)
      return null;
    throw err;
  }
}

export async function listCatalogueItems(
  options: CatalogueItemListOptions = {},
): Promise<ListResponse<CatalogueItemTile>> {
  const search = new URLSearchParams();
  if (options.page != null) search.set("page", String(options.page));
  if (options.pageSize != null)
    search.set("page_size", String(options.pageSize));
  if (options.sort != null) search.set("sort", options.sort);
  if (options.q) search.set("q", options.q);
  for (const id of options.catalogueIds ?? [])
    search.append("catalogue_id", id);
  if (options.favouritesOnly) search.set("favourites_only", "true");
  if (options.orderingCustomerCode !== undefined)
    search.set("ordering_customer_code", options.orderingCustomerCode);
  const qs = search.toString();
  const page = await apiClient.get<PaginatedCatalogueItemTiles>(
    `catalogue/items${qs ? `?${qs}` : ""}`,
  );
  return {
    items: page.items.map(tileFromSchema),
    total: page.total,
    page: page.page,
    pageSize: page.page_size,
    totalPages: page.total_pages,
  };
}

export async function favouriteCatalogueItem(itemId: string): Promise<void> {
  await apiClient.post<void>(`catalogue/items/${itemId}/actions/favourite`, {});
}

export async function unfavouriteCatalogueItem(itemId: string): Promise<void> {
  await apiClient.post<void>(
    `catalogue/items/${itemId}/actions/unfavourite`,
    {},
  );
}

export async function getResolvedCatalogueItem(
  itemId: string,
): Promise<{ catalogue: Catalogue; item: CatalogueItem } | null> {
  try {
    const schema = await apiClient.get<ResolvedCatalogueItemSchema>(
      `catalogue/items/${itemId}`,
    );
    return {
      catalogue: catalogueFromSchema(schema.catalogue),
      item: resolvedItemFromSchema(schema),
    };
  } catch (err: unknown) {
    if (err && typeof err === "object" && "status" in err && err.status === 404)
      return null;
    throw err;
  }
}

// ── Mappers ──────────────────────────────────────────────────────────────────

function catalogueFromSchema(s: CatalogueSchema): Catalogue {
  // Absent on the wire = every route (#87); only a real narrowing is carried.
  const routeNarrowing = routeNarrowingFromSchema(s.route_narrowing);
  const narrowed = routeNarrowing ? { routeNarrowing } : {};
  // A private catalogue is named after its customer, so its name stands for the customer's.
  const privateTo = s.customer_code
    ? { privateTo: { customerCode: s.customer_code, customerName: s.name } }
    : {};
  return {
    id: s.id,
    name: s.name,
    audience: s.audience,
    ...privateTo,
    accountIds: s.account_ids ?? [],
    ownedBy: s.owned_by,
    ...narrowed,
  };
}

/** A wire route narrowing as the frontend's, or undefined when it allows every route. */
function routeNarrowingFromSchema(
  s: RouteNarrowingSchema | null | undefined,
): RouteNarrowing | undefined {
  if (
    !s ||
    (!s.pinned_route &&
      !(s.allowed_routes ?? []).length &&
      s.allowed_speeds == null)
  )
    return undefined;
  return {
    allowedRoutes: s.allowed_routes ?? [],
    pinnedRoute: s.pinned_route ?? null,
    ...(s.allowed_speeds != null ? { allowedSpeeds: s.allowed_speeds } : {}),
  };
}

function routeNarrowingToWire(n: RouteNarrowing): RouteNarrowingSchema {
  return {
    allowed_routes: n.allowedRoutes ?? [],
    pinned_route: n.pinnedRoute ?? null,
    allowed_speeds: n.allowedSpeeds ?? null,
  };
}

function itemTileFromSchema(s: CatalogueItemSchema): CatalogueItem {
  return {
    id: s.id,
    name: s.name,
    catalogueId: s.catalogue_id,
    productTypeId: s.product_type_id,
    productLabel: s.product_label,
    fulfilment: s.fulfilment as FulfilmentType,
    defaultQuantity: s.default_quantity,
    artworkModes: s.artwork_modes as ArtworkMode[],
    derivesFromDictionaryItemId: s.derives_from_dictionary_item_id ?? undefined,
    derivesFromCatalogueItemId: s.derives_from_catalogue_item_id ?? undefined,
    narrowing: narrowingFromSchema(s.narrowing),
    ...presentationFromSchema(s),
    ...kindFromSchema(s),
    note: s.note ?? undefined,
    customerProductRef: s.customer_product_ref ?? undefined,
    sortOrder: s.sort_order,
    priceGroupId: s.price_group_id ?? undefined,
    vatTreatment: s.vat_treatment ?? null,
    // Grid tiles don't render fields or pricing — populated on the detail page.
    fields: [],
    quantityBreaks: [],
  };
}

/** The customer-facing presentation of an item: display values, the item's own values, and related items. */
function presentationFromSchema(
  s: CatalogueItemSchema,
): Pick<
  CatalogueItem,
  | "icon"
  | "specSummary"
  | "description"
  | "specList"
  | "relatedItemIds"
  | "ownIcon"
  | "ownSpecSummary"
  | "ownDescription"
  | "ownSpecList"
> {
  return {
    icon: s.icon ?? undefined,
    specSummary: s.spec_summary ?? undefined,
    description: s.description ?? undefined,
    specList: (s.spec_list ?? []).map(specListEntryFromSchema),
    relatedItemIds: s.related_item_ids ?? [],
    ownIcon: s.own_icon ?? undefined,
    ownSpecSummary: s.own_spec_summary ?? undefined,
    ownDescription: s.own_description ?? undefined,
    ownSpecList: (s.own_spec_list ?? []).map(specListEntryFromSchema),
  };
}

/** Whether the item is repeatable, and whether its artwork expires (both declared by code). */
function kindFromSchema(
  s: CatalogueItemSchema,
): Pick<CatalogueItem, "kind" | "artworkExpires"> {
  return {
    kind: s.kind ?? "configurable",
    artworkExpires: s.artwork_expires ?? false,
  };
}

function specListEntryFromSchema(e: { label: string; value: string }) {
  return { label: e.label, value: e.value };
}

function tileFromSchema(s: CatalogueItemTileSchema): CatalogueItemTile {
  const item = itemTileFromSchema(s.item);
  return {
    // The listing ships real quantity breaks — the standard-quantity rungs,
    // not a price — so the tile's Quantity chips render; fields stay empty —
    // needsSetup carries the one derivation the tile needs from
    // them. The tile's actual price is indicativePricePence below, resolved
    // server-side from cached manufacturing cost.
    item: { ...item, quantityBreaks: s.quantity_breaks.map(breakFromSchema) },
    catalogueName: s.catalogue_name,
    needsSetup: s.needs_setup,
    isFavourite: s.is_favourite,
    fieldCount: s.field_count,
    indicativePricePence: s.indicative_price_pence ?? null,
  };
}

export function resolvedItemFromSchema(
  s: ResolvedCatalogueItemSchema,
): CatalogueItem {
  const item = s.item;
  return {
    id: item.id,
    name: item.name,
    catalogueId: item.catalogue_id,
    productTypeId: item.product_type_id,
    productLabel: item.product_label,
    fulfilment: item.fulfilment as FulfilmentType,
    defaultQuantity: item.default_quantity,
    artworkModes: item.artwork_modes as ArtworkMode[],
    derivesFromDictionaryItemId:
      item.derives_from_dictionary_item_id ?? undefined,
    derivesFromCatalogueItemId:
      item.derives_from_catalogue_item_id ?? undefined,
    narrowing: narrowingFromSchema(item.narrowing),
    ...presentationFromSchema(item),
    ...kindFromSchema(item),
    note: item.note ?? undefined,
    customerProductRef: item.customer_product_ref ?? undefined,
    sortOrder: item.sort_order,
    priceGroupId: item.price_group_id ?? undefined,
    vatTreatment: item.vat_treatment ?? null,
    ...staffMigrationFactsFromSchema(s),
    fields: s.fields.map(fieldFromSchema),
    quantityBreaks: s.quantity_breaks.map(breakFromSchema),
  };
}

/** The staff-only route hint and Tharstern provenance, present only on the staff item read. */
function staffMigrationFactsFromSchema(
  s: ResolvedCatalogueItemSchema,
): Pick<CatalogueItem, "routeHint" | "tharsternProvenance"> {
  const hint = s.route_hint;
  const provenance = s.tharstern_provenance;
  return {
    routeHint: hint
      ? {
          route: hint.route,
          supplierName: hint.supplier_name ?? undefined,
          costCentreCode: hint.cost_centre_code ?? undefined,
          lastJobNo: hint.last_job_no ?? undefined,
          lastOn: hint.last_on ?? undefined,
        }
      : undefined,
    tharsternProvenance: provenance
      ? {
          stockCode: provenance.stock_code ?? undefined,
          templateEstimateId: provenance.template_estimate_id ?? undefined,
          jobNos: provenance.job_nos ?? [],
          lastJobNo: provenance.last_job_no ?? undefined,
        }
      : undefined,
  };
}

function narrowingFromSchema(s: NarrowingSchema | undefined): Narrowing {
  if (!s) return {};
  const fields: Record<string, FieldNarrowing> = {};
  for (const [key, fn] of Object.entries(s.fields ?? {})) {
    fields[key] = {
      allowedOptions: fn.allowed_options,
      pinned: fn.pinned ?? undefined,
    };
  }
  const routes = routeNarrowingFromSchema(s.routes);
  return { fields, quantities: s.quantities, ...(routes ? { routes } : {}) };
}

function narrowingToRequestBody(n: Narrowing | undefined): object {
  const fields: Record<
    string,
    { allowed_options?: string[]; pinned?: string | null }
  > = {};
  for (const [key, fn] of Object.entries(n?.fields ?? {})) {
    fields[key] = {
      allowed_options: fn.allowedOptions ?? [],
      pinned: fn.pinned ?? null,
    };
  }
  // Round-tripped so an edit to fields or quantities never drops the item's
  // route narrowing (#87): the update replaces the whole narrowing.
  return {
    fields,
    quantities: n?.quantities ?? [],
    ...(n?.routes ? { routes: routeNarrowingToWire(n.routes) } : {}),
  };
}

function dictionaryItemFromSchema(s: DictionaryItemSchema): DictionaryItem {
  return {
    id: s.id,
    name: s.name,
    productTypeId: s.product_type_id,
    fulfilment: s.fulfilment as FulfilmentType,
    defaultVatTreatment: s.default_vat_treatment ?? null,
  };
}

function dictionaryItemDetailFromSchema(
  s: DictionaryItemDetailSchema,
): DictionaryItemDetail {
  return {
    dictionaryItem: dictionaryItemFromSchema(s.dictionary_item),
    fields: s.fields.map(fieldFromSchema),
    quantityBreaks: s.quantity_breaks.map(breakFromSchema),
  };
}

export function fieldFromSchema(f: CatalogueFieldSchema): CatalogueField {
  return {
    key: f.key,
    label: f.label,
    mode: f.mode as FieldMode,
    control: f.control as FieldControl,
    fixedValue: f.fixed_value ?? undefined,
    step: f.step ?? undefined,
    min: f.min_value ?? undefined,
    max: f.max_value ?? undefined,
    section: f.section ?? undefined,
    customHint: f.custom_hint ?? undefined,
    customLabel: f.custom_label ?? undefined,
    affectsCost: f.affects_cost,
    customValueType: f.custom_value_type ?? undefined,
    customConstraints:
      (f.custom_constraints as CustomValueConstraints | null) ?? undefined,
    customMapping: f.custom_mapping ?? undefined,
    options: f.options.map((o) => ({
      code: o.code,
      label: o.label,
      isDefault: o.is_default,
      priceDelta:
        o.price_delta_pence > 0 ? o.price_delta_pence / 100 : undefined,
      submitMapping: o.submit_mapping ?? undefined,
      requires:
        o.requires && Object.keys(o.requires).length > 0
          ? o.requires
          : undefined,
      paper: o.paper
        ? {
            typeCode: o.paper.type_code,
            typeLabel: o.paper.type_label,
            weight: o.paper.weight,
            weightLabel: o.paper.weight_label,
          }
        : undefined,
    })),
  };
}

function breakFromSchema(b: {
  id: string;
  qty: number;
  price_pence: number;
  per_unit_pence: number;
}): QuantityBreak {
  return { qty: b.qty, price: b.price_pence / 100 };
}

/**
 * The catalogue item page's delivery address picker: one page of the
 * customer's active delivery addresses from the customer spine, plus the
 * customer's CIS code (what a captured address is created against).
 */
export async function listCatalogueDeliveryAddresses(
  customerCode: string,
  options: Pick<ListOptions, "q" | "page" | "pageSize"> = {},
): Promise<CatalogueAddressBook> {
  const search = new URLSearchParams();
  if (options.q) search.set("q", options.q);
  if (options.page) search.set("page", String(options.page));
  if (options.pageSize) search.set("page_size", String(options.pageSize));
  const query = search.toString();
  const path = `catalogue/customers/${encodeURIComponent(customerCode)}/delivery-addresses`;
  return apiClient.get<CatalogueAddressBook>(query ? `${path}?${query}` : path);
}

/** Our collection points, for the delivery section's Collect tab (doc 104 §7); empty until operations add them. */
export async function listCollectionPoints(): Promise<CollectionPoint[]> {
  return apiClient.get<CollectionPoint[]>("catalogue/collection-points");
}

/**
 * Live price preview with cost lineage, for the internal/CSR ordering UI.
 * Never rejects on a blocked/approval_required verdict — the backend
 * returns those inline for the caller to render, since the caller hasn't
 * committed to anything yet.
 *
 * `customerCode` is optional: absent, the backend prices against the
 * central-scope price group. The manufacturing cost is keyed on variant ×
 * quantity, so the answer is a real price either way.
 */
/**
 * Presentation kind for a breakdown line, from its pricing factor and amount.
 *
 * The cost and base rows are the price being built up (no sign); everything
 * else is a movement against it, signed by its own amount. Shared by the real
 * adapter and the mock so both render identically.
 */
export function quoteLineKindForFactor(
  factor: string,
  amountPence: number,
): QuoteLine["kind"] {
  if (factor === "base" || factor === "cost") return "base";
  return amountPence < 0 ? "discount" : "add";
}

/**
 * Wire `PriceProvenanceSchema` (one shape, optional fields per source) into
 * the frontend's `PriceProvenance` discriminated union (cost-engine-
 * productionisation spec 06 §5.8/§14). Null when the quote has no
 * provenance (the manual quantity-break fallback).
 */
function priceProvenanceFromWire(
  p: components["schemas"]["PriceProvenanceSchema"] | null | undefined,
): PriceProvenance | null {
  if (!p) return null;
  if (p.source === "cis_engine") {
    return {
      source: "cis_engine",
      costedAt: p.costed_at ?? "",
      checksPassed: p.checks_passed ?? false,
    };
  }
  if (p.source === "supplier_quote") {
    return {
      source: "supplier_quote",
      routeLabel: p.route_label ?? p.fulfilment_route_code ?? "",
      tier: p.turnaround_tier ?? "",
      fetchedAt: p.fetched_at ?? "",
    };
  }
  return { source: "tharstern_estimate", fetchedAt: p.fetched_at ?? "" };
}

function priceOptionDeliveryFromWire(
  d: PriceOptionDeliverySchema,
): PriceOptionDelivery {
  return d.kind === "priced" && d.delivery_price_pence != null
    ? { kind: "priced", deliveryPricePence: d.delivery_price_pence }
    : d.kind === "priced"
      ? { kind: "at_address" }
      : { kind: d.kind };
}

function optionArrivalFromWire(a: OptionArrivalSchema): OptionArrival {
  return {
    arrivesOn: a.arrives_on,
    arrivesBy: a.arrives_by ?? null,
    arrivesWithoutBufferOn: a.arrives_without_buffer_on,
    isEstimate: a.is_estimate,
    basis: a.basis,
    approveBy: a.approve_by ?? null,
    daysToSpare: a.days_to_spare ?? null,
    sentence: a.sentence,
    dateFrom: a.date_from,
    approveBySentence: a.approve_by_sentence ?? null,
  };
}

/** The wire Speeds as the frontend's. Exported for the mock adapter's contract test. */
export function speedChoiceFromWire(s: SpeedChoiceSchema): SpeedChoice {
  return {
    offers: s.offers.map((o) => ({
      speedCode: o.speed_code,
      label: o.label,
      workingDaysToArrival: o.working_days_to_arrival,
      optionKey: o.option_key,
      arrival: optionArrivalFromWire(o.arrival),
      pricePence: o.price_pence,
      comparablePence: o.comparable_pence ?? null,
      upliftPence: o.uplift_pence,
      ruleNote: o.rule_note ?? null,
      isToBeConfirmed: o.is_to_be_confirmed,
      arrivesInTime: o.arrives_in_time,
      sentence: o.sentence,
    })),
    unavailable: s.unavailable.map((u) => ({
      speedCode: u.speed_code,
      label: u.label,
      reason: u.reason,
    })),
    baselineCode: s.baseline_code ?? null,
    defaultCode: s.default_code ?? null,
    defaultReason: s.default_reason,
    selectedCode: s.selected_code ?? null,
    quickerCode: s.quicker_code ?? null,
    neededBy: s.needed_by ?? null,
    neededByIsHard: s.needed_by_is_hard,
  };
}

function priceOptionFromWire(o: PriceOptionSchema): CataloguePriceOption {
  return {
    key: o.key,
    routeCode: o.route_code,
    routeClass: o.route_class,
    routeLabel: o.route_label,
    tier: o.tier,
    arrival: o.arrival ? optionArrivalFromWire(o.arrival) : null,
    speedCode: o.speed_code ?? null,
    noArrivalReason: o.no_arrival_reason ?? null,
    pricePence: o.price_pence,
    costPence: o.cost_pence ?? null,
    contributionPct: o.contribution_pct ?? null,
    delivery: priceOptionDeliveryFromWire(o.delivery),
    comparablePence: o.comparable_pence ?? null,
    isExpiring: o.is_expiring,
    arrivesInTime: o.arrives_in_time,
    listNotice: o.list_notice ?? undefined,
    ...(o.version_quantities?.length
      ? {
          versionQuantities: o.version_quantities,
          versionPrices: o.version_prices,
        }
      : {}),
  };
}

/**
 * Wire price options, or null. The backend refuses a preview with nothing on
 * offer, so a present block always has a default and a selection.
 */
function priceOptionsFromWire(
  s: PriceOptionsSchema | null | undefined,
): CataloguePriceOptions | null {
  if (!s || s.default_key == null || s.selected_key == null) return null;
  return {
    options: s.options.map(priceOptionFromWire),
    defaultKey: s.default_key,
    defaultReason: s.default_reason,
    isProvisional: s.is_provisional,
    selectedKey: s.selected_key,
    unavailable: s.unavailable.map((u) => ({
      routeCode: u.route_code,
      routeLabel: u.route_label,
      reason: u.reason,
      isDeliveryNotPriced: u.is_delivery_not_priced ?? false,
    })),
    chosenWasDropped: s.chosen_was_dropped ?? false,
    overrideRequirement: s.override_requirement
      ? {
          reasonPrompt: s.override_requirement.reason_prompt,
          noteRequiredFor: s.override_requirement.note_required_for,
          notePrompt: s.override_requirement.note_prompt,
          suggestedReason: s.override_requirement.suggested_reason ?? null,
          suggestedNote: s.override_requirement.suggested_note ?? null,
        }
      : null,
    pending: (s.pending ?? []).map((p) => ({
      routeCode: p.route_code,
      routeLabel: p.route_label,
      retryAfterSeconds: p.retry_after_seconds,
    })),
  };
}

/** The `price-preview` request: `PricePreviewRequest` on the wire
 *  (`modules/catalogue_module/api/schemas/catalogue.py`), plus `ladder`
 *  (`LivePricePreviewRequest`). */
type PricePreviewRequestInput = {
  configuration: Record<string, string>;
  quantity: number;
  /** The Speed to headline (Time's code); null = the default Speed (d59). */
  speedCode?: string | null;
  customerCode?: string | null;
  // The destination (or collection point) and the staff's courier choice.
  // Omitted, the preview answers `address_required` and prices no delivery.
  delivery?: CatalogueDeliverySelection | null;
  // The route option to headline behind the Speed (#2). Ignored by the
  // server while supplier pricing is hidden.
  priceOption?: PriceOptionChoice | null;
  /** ISO date the order is needed by, and whether it is fixed (N3). */
  neededBy?: string | null;
  neededByIsHard?: boolean;
  /** A line of several versions (the configuration's `Versions` above 1): how its quantity is shared (doc 108). */
  versionSplit?: "even" | "entered";
  versionQuantities?: number[];
};

function pricePreviewRequestBody(req: PricePreviewRequestInput) {
  return {
    ...(req.versionSplit ? { version_split: req.versionSplit } : {}),
    ...(req.versionQuantities
      ? { version_quantities: req.versionQuantities }
      : {}),
    ...(req.priceOption
      ? {
          price_option: {
            route_code: req.priceOption.routeCode,
            tier: req.priceOption.tier,
          },
        }
      : {}),
    configuration: req.configuration,
    quantity: req.quantity,
    speed_code: req.speedCode ?? null,
    needed_by: req.neededBy ?? null,
    needed_by_is_hard: req.neededByIsHard ?? false,
    customer_code: req.customerCode ?? null,
    // `mode` tells the sourcing policy that collection and hold ship nothing
    // (no delivery line, no country rule).
    delivery: req.delivery ? deliverySelectionBody(req.delivery) : null,
  };
}

/** A percent on the wire as the fraction the price level picker formats (the mock's unit). */
const percentAsFraction = (pct: number | null | undefined): number | null =>
  pct == null ? null : pct / 100;

/**
 * The headline's price levels, as the picker reads them. The backend states
 * margins in percent; the picker and the mock adapter use fractions, so the
 * three margins convert here and nowhere else.
 */
export function priceLevelsFromWire(w: PriceLevelsWire): PriceLevels {
  return {
    suggested: w.suggested,
    unrecognised: w.unrecognised ?? false,
    work_style: w.work_style ?? "",
    work_style_floor_pct: percentAsFraction(w.work_style_floor_pct),
    drop_to_policy: w.drop_to_policy ?? null,
    levels: (w.levels ?? []).map((l) => ({
      kind: l.kind,
      price_pence: l.price_pence,
      unit_price_pence: l.unit_price_pence,
      source: l.source ?? null,
      contribution_margin_pct: percentAsFraction(l.contribution_margin_pct),
      verdict: l.verdict ?? "unchecked",
      verdict_reason: l.verdict_reason ?? null,
      suggestable: l.suggestable ?? false,
      not_suggestable_reason: l.not_suggestable_reason ?? null,
      ...(l.contract
        ? { contract: { ...l.contract, note: l.contract.note ?? null } }
        : {}),
      ...(l.levers ? { levers: l.levers } : {}),
      similar: l.similar
        ? {
            band: l.similar.band,
            carried_margin_pct: percentAsFraction(l.similar.carried_margin_pct),
            quantity_ratio: l.similar.quantity_ratio,
            source_pages: l.similar.source_pages ?? null,
            pages: l.similar.pages ?? null,
            pages_ratio: l.similar.pages_ratio,
            source_stock_group: l.similar.source_stock_group ?? null,
            stock_group: l.similar.stock_group ?? null,
            source_size: l.similar.source_size ?? null,
            size: l.similar.size ?? null,
            area_ratio: l.similar.area_ratio ?? 1,
            basis: l.similar.basis ?? "charged",
            market_cost_pence: l.similar.market_cost_pence ?? null,
          }
        : null,
      stock_confirmed: l.stock_confirmed ?? null,
    })),
  };
}

/** The `PricePreviewResponseSchema` mapping (the backend's `price_preview_to_schema`). */
export function pricePreviewFromSchema(
  s: PricePreviewResponseSchema,
): CatalogueItemPricePreview {
  return {
    quotedPence: s.quoted_pence,
    perUnitPence: s.per_unit_pence,
    verdictStatus:
      s.verdict_status as CatalogueItemPricePreview["verdictStatus"],
    priceGroupName: s.price_group_name ?? "",
    priceGroupId: s.price_group_id ?? null,
    dictionaryItemId: s.dictionary_item_id ?? null,
    // The catalogue preview states every amount in pence with an explicit
    // `_pence` suffix, unlike pricing_module's quote schema which uses pounds.
    // The frontend's QuoteLine is pounds, so convert here rather than reusing
    // pricing's mappers — the differing field names are what stopped this
    // being a silent 100x error.
    // `kind` on the wire is the pricing *factor*; QuoteLine.kind is a
    // presentation kind (sign/colour). Carry the factor in `source` and derive
    // the presentation kind from the amount's sign — the previous
    // `l.kind as QuoteLine["kind"]` cast asserted a factor name was one of
    // base/add/discount/subtotal, which it never was.
    lines: (s.lines ?? []).map((l) => ({
      label: l.label,
      kind: quoteLineKindForFactor(l.kind, l.amount_pence),
      amount: l.amount_pence / 100,
      source: l.kind,
      note: l.note ?? null,
      applied: l.applied,
    })),
    speeds: speedChoiceFromWire(s.speeds),
    baseSource: s.base_source,
    costSnapshotId: s.cost_snapshot_id ?? null,
    variantId: s.variant_id ?? null,
    costStatus: s.cost_status ?? null,
    costStaleReason: s.cost_stale_reason ?? null,
    costRefreshedAt: s.cost_refreshed_at ?? null,
    tharsternEstimateRef: s.tharstern_estimate_ref ?? null,
    weight: s.weight ?? null,
    payloadHash: s.payload_hash ?? null,
    // Pence stays pence: the ladder renders currency from `quotedPence`, and a
    // null price is the backend saying this quantity has no usable cost — it
    // must survive the mapping as null rather than becoming a zero.
    verdictReason: s.verdict_reason ?? null,
    contributionMarginPct: s.contribution_margin_pct ?? null,
    concessionPct: s.concession_pct ?? null,
    manufacturingCostPence: s.manufacturing_cost_pence ?? null,
    purchasesPence: s.purchases_pence ?? null,
    wagesPence: s.wages_pence ?? null,
    tharsternSellPricePence: s.tharstern_sell_price_pence ?? null,
    costDrilldownLine: s.cost_drilldown_line ?? null,
    priceProvenance: priceProvenanceFromWire(s.price_provenance),
    quantityLadder: (s.quantity_ladder ?? []).map((row) => ({
      quantity: row.quantity,
      availability: row.availability,
      quotedPence: row.quoted_pence ?? null,
      savingPct: row.saving_pct ?? null,
      isBestValue: row.is_best_value,
      isSelected: row.is_selected,
      costStatus: row.cost_status ?? null,
    })),
    delivery: deliveryFromWire(s.delivery),
    destinationDeliveries: (s.destination_deliveries ?? []).map(
      deliveryFromWire,
    ),
    totalWithDeliveryPence: s.total_with_delivery_pence ?? null,
    deliveryNotPricedWarnings: s.delivery_not_priced_warnings ?? [],
    isDeliveryUnpriced: s.is_delivery_unpriced ?? false,
    // Absent while supplier pricing is hidden: the page is today's (#90).
    ...(s.price_options
      ? { priceOptions: priceOptionsFromWire(s.price_options) }
      : {}),
    // The price hierarchy and the precedent it came from: without them the
    // picker could offer only the policy price, whatever the customer paid.
    ...(s.levels ? { priceLevels: priceLevelsFromWire(s.levels) } : {}),
    ...(s.precedent ? { precedent: s.precedent } : {}),
  };
}

export async function getCatalogueItemPricePreview(
  itemId: string,
  req: PricePreviewRequestInput & {
    // False prices only `quantity`: the headline plus, when that quantity is
    // a standard break, its one ladder row. The ordering page asks for that
    // first so the price the orderer is waiting for is not queued behind nine
    // quantities nobody has asked about.
    ladder?: boolean;
  },
): Promise<CatalogueItemPricePreview> {
  const s = await apiClient.post<PricePreviewResponseSchema>(
    `catalogue/items/${itemId}/price-preview`,
    { ...pricePreviewRequestBody(req), ladder: req.ladder ?? true },
  );
  return pricePreviewFromSchema(s);
}

/**
 * Resolve one (configuration, quantity) pair's manufacturing cost,
 * live-costing on demand — see `DataAdapter.costCatalogueConfiguration`.
 */
export async function costCatalogueConfiguration(
  itemId: string,
  req: { configuration: Record<string, string>; quantity: number },
): Promise<VariantQuantityCost> {
  const s = await apiClient.post<VariantQuantityCostSchema>(
    `catalogue/items/${itemId}/actions/cost-configuration`,
    { configuration: req.configuration, quantity: req.quantity },
  );
  return variantQuantityCostFromSchema(s);
}

/**
 * Trigger a variant-aware Tharstern cost refresh, scoped by the passed scope:
 * whole item (all active variants × quantity breaks), a single variant, or a
 * single (variant, quantity) pair. Returns the run id to poll.
 */
export async function triggerCatalogueCostRefresh(
  itemId: string,
  scope: { variantId?: string; quantity?: number; confirm?: boolean },
): Promise<{ runId: string }> {
  const body: Record<string, unknown> = {};
  if (scope.variantId) body.variant_id = scope.variantId;
  if (scope.quantity != null) body.quantity = scope.quantity;
  if (scope.confirm) body.confirm = scope.confirm;
  const data = await apiClient.post<CostRefreshAcceptedResponse>(
    `dictionary/items/${itemId}/cost-refreshes`,
    body,
  );
  return { runId: data.run_id };
}

export async function getCatalogueCostRefreshRun(
  runId: string,
): Promise<CatalogueCostRefreshRun> {
  const s = await apiClient.get<CostRefreshRunStatusSchema>(
    `dictionary/cost-refreshes/${runId}`,
  );
  return {
    runId: s.run_id,
    // Dictionary-grain now (design doc "the dictionary rewire") — the field
    // still carries a catalogueItemId name on the frontend domain type until
    // the curator surfaces that consume it are rebuilt for the dictionary
    // model (a separate, deferred task).
    catalogueItemId: s.dictionary_item_id,
    variantId: s.variant_id ?? null,
    quantity: s.quantity ?? null,
    status: s.status,
    triggerSource: s.trigger_source,
    startedAt: s.started_at,
    completedAt: s.completed_at ?? null,
    totalPairs: s.total_pairs,
    succeededCount: s.succeeded_count,
    failedCount: s.failed_count,
    skippedCount: s.skipped_count,
    failureReason: s.failure_reason ?? null,
  };
}

export async function getCatalogueCostLineage(
  costId: string,
): Promise<CatalogueCostLineage> {
  const s = await apiClient.get<CostLineageSchema>(
    `dictionary/variant-quantity-costs/${costId}/lineage`,
  );
  return {
    costId: s.cost_id,
    submittedPayload: s.submitted_payload,
    estimateResponse: s.estimate_response ?? null,
  };
}

export async function listCatalogueItemVariants(
  itemId: string,
): Promise<CatalogueItemVariantWithCosts[]> {
  const schemas = await apiClient.get<CatalogueItemVariantSchema[]>(
    `dictionary/items/${itemId}/variants`,
  );
  return schemas.map(variantWithCostsFromSchema);
}

function variantWithCostsFromSchema(
  s: CatalogueItemVariantSchema,
): CatalogueItemVariantWithCosts {
  return {
    id: s.id,
    catalogueItemId: s.dictionary_item_id,
    configuration: s.configuration,
    configurationHash: s.configuration_hash,
    displayLabel: s.display_label,
    isActive: s.is_active,
    sortOrder: s.sort_order,
    quantityCosts: (s.quantity_costs ?? []).map(variantQuantityCostFromSchema),
  };
}

function variantQuantityCostFromSchema(
  c: VariantQuantityCostSchema,
): VariantQuantityCost {
  return {
    id: c.id,
    variantId: c.variant_id,
    quantity: c.quantity,
    status: c.status,
    costPence: c.cost_pence ?? null,
    weight: c.weight ?? null,
    tharsternSellPricePence: c.tharstern_sell_price_pence ?? null,
    purchasesPence: c.purchases_pence ?? null,
    wagesPence: c.wages_pence ?? null,
    tharsternEstimateId: c.tharstern_estimate_id ?? null,
    tharsternEstimateRef: c.tharstern_estimate_ref ?? null,
    payloadHash: c.payload_hash ?? null,
    schemaHash: c.schema_hash ?? null,
    staleReason: c.stale_reason ?? null,
    errorReason: c.error_reason ?? null,
    refreshedAt: c.refreshed_at ?? null,
    lastAttemptedAt: c.last_attempted_at ?? null,
    attemptCount: c.attempt_count,
  };
}

// ── Management API (derive / presentation update) ─────────────────────────────

function presentationInputToRequestBody(
  input: CatalogueItemPresentationInput,
): object {
  return {
    name: input.name,
    product_label: input.productLabel,
    default_quantity: input.defaultQuantity,
    artwork_modes: input.artworkModes,
    icon: input.icon ?? null,
    spec_summary: input.specSummary ?? null,
    description: input.description ?? null,
    spec_list: input.specList.map((e) => ({ label: e.label, value: e.value })),
    related_item_ids: input.relatedItemIds,
    note: input.note ?? null,
    sort_order: input.sortOrder ?? 0,
    price_group_id: input.priceGroupId ?? null,
    vat_treatment: input.vatTreatment ?? null,
    narrowing: narrowingToRequestBody(input.narrowing),
    customer_product_ref: input.customerProductRef ?? null,
  };
}

/** The dictionary item's full (unnarrowed) fields/options and quantity ladder — for spec editing. */
export async function getDictionaryItemDetail(
  dictionaryItemId: string,
): Promise<DictionaryItemDetail | null> {
  try {
    const schema = await apiClient.get<DictionaryItemDetailSchema>(
      `dictionary/items/${dictionaryItemId}`,
    );
    return dictionaryItemDetailFromSchema(schema);
  } catch (err: unknown) {
    if (err && typeof err === "object" && "status" in err && err.status === 404)
      return null;
    throw err;
  }
}

/**
 * Derive a catalogue item from a dictionary item or another catalogue item,
 * with presentation fields + narrowing. Also how core range items are
 * made — `targetCatalogueId` is the catalogue the new item lands in, which
 * may be the core range or a customer catalogue; the source is named
 * in `input`.
 */
export async function deriveCatalogueItem(
  targetCatalogueId: string,
  input: DeriveCatalogueItemInput,
): Promise<ManagementResult> {
  const schema = await apiClient.post<ResolvedCatalogueItemSchema>(
    `catalogue/catalogues/${targetCatalogueId}/items/actions/derive`,
    {
      ...presentationInputToRequestBody(input),
      source_dictionary_item_id: input.sourceDictionaryItemId ?? null,
      source_catalogue_item_id: input.sourceCatalogueItemId ?? null,
    },
  );
  return {
    catalogue: catalogueFromSchema(schema.catalogue),
    item: resolvedItemFromSchema(schema),
  };
}

/** Edit an existing catalogue item's presentation and narrowing (never its spec). */
export async function updateCatalogueItem(
  catalogueId: string,
  itemId: string,
  patch: CatalogueItemPresentationInput,
): Promise<ManagementResult> {
  const schema = await apiClient.post<ResolvedCatalogueItemSchema>(
    `catalogue/catalogues/${catalogueId}/items/${itemId}/actions/update`,
    presentationInputToRequestBody(patch),
  );
  return {
    catalogue: catalogueFromSchema(schema.catalogue),
    item: resolvedItemFromSchema(schema),
  };
}

function customerCatalogueSettingsFromSchema(
  s: CustomerCatalogueSettingsResponse,
): CustomerCatalogueSettings {
  return {
    customerId: s.customer_id,
    requiresCustomerProductRef: s.requires_customer_product_ref,
    updatedAt: s.updated_at ?? null,
    canManage: s.can_manage,
  };
}

/** See `DataAdapter.getCustomerCatalogueSettings`. */
export async function getCustomerCatalogueSettings(
  customerCode: string,
): Promise<CustomerCatalogueSettings> {
  const schema = await apiClient.get<CustomerCatalogueSettingsResponse>(
    `catalogue/customers/${encodeURIComponent(customerCode)}/catalogue-settings`,
  );
  return customerCatalogueSettingsFromSchema(schema);
}

/** See `DataAdapter.setCustomerProductRefRequired`. */
export async function setCustomerProductRefRequired(
  customerCode: string,
  required: boolean,
): Promise<CustomerCatalogueSettings> {
  const schema = await apiClient.post<CustomerCatalogueSettingsResponse>(
    `catalogue/customers/${encodeURIComponent(customerCode)}/catalogue-settings/actions/set-require-customer-product-ref`,
    { required },
  );
  return customerCatalogueSettingsFromSchema(schema);
}

/** See `DataAdapter.updateCatalogueRouteNarrowing`. */
export async function updateCatalogueRouteNarrowing(
  catalogueId: string,
  routes: RouteNarrowing,
): Promise<Catalogue> {
  const schema = await apiClient.post<CatalogueSchema>(
    `catalogue/catalogues/${encodeURIComponent(catalogueId)}/actions/update-route-narrowing`,
    routeNarrowingToWire(routes),
  );
  return catalogueFromSchema(schema);
}

/** See `DataAdapter.listSpeeds`. */
export async function listSpeeds(): Promise<SpeedLadderRung[]> {
  const data = await apiClient.get<CatalogueSpeedSchema[]>("catalogue/speeds");
  return data.map((s) => ({
    speedCode: s.speed_code,
    label: s.label,
    workingDaysToArrival: s.working_days_to_arrival,
    needsAPerson: s.needs_a_person,
  }));
}

/** See `DataAdapter.getRouteNarrowingConsequences`. */
export async function getRouteNarrowingConsequences(input: {
  routes: RouteNarrowing;
  catalogueId?: string;
  catalogueItemId?: string;
}): Promise<string[]> {
  const data = await apiClient.post<RouteNarrowingConsequencesResponse>(
    "catalogue/route-narrowing/consequences",
    {
      route_narrowing: routeNarrowingToWire(input.routes),
      catalogue_id: input.catalogueId ?? null,
      catalogue_item_id: input.catalogueItemId ?? null,
    },
  );
  return data.lines;
}
